import React, { useEffect, useState, useMemo } from "react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Search, Archive, Edit3, Save, X, Dumbbell } from "lucide-react";

const empty = {
  name: "", primary_muscle: "", muscle_groups: [], equipment: [], execution_type: "",
  difficulty: "", tags: [], video_url: "", description: "", tempo_default: "", rest_default: "",
};

export default function AdminExerciseCatalog() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ muscle_groups: [], equipment: [], execution_types: [], difficulty: [] });
  const [q, setQ] = useState("");
  const [muscle, setMuscle] = useState("");
  const [equipment, setEquipment] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | {id, ...} for existing
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (muscle) params.set("muscle", muscle);
      if (equipment) params.set("equipment", equipment);
      const { data } = await api.get(`/admin/exercise-catalog${params.toString() ? "?" + params : ""}`);
      setItems(data.filter((x) => !x.archived_at));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/exercise-catalog-meta"); setMeta(data); } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); }, [muscle, equipment]);

  const startNew = () => setEditing({ ...empty });
  const startEdit = (it) => setEditing({ ...empty, ...it });

  const toggleArr = (field, val) => {
    setEditing((p) => {
      const arr = new Set(p[field] || []);
      if (arr.has(val)) arr.delete(val); else arr.add(val);
      return { ...p, [field]: Array.from(arr) };
    });
  };

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const payload = { ...editing };
      // Drop empty strings so 422 doesn't trigger
      for (const k of ["primary_muscle","execution_type","difficulty","video_url","description","tempo_default","rest_default"]) {
        if (!payload[k]) delete payload[k];
      }
      if (editing.id) await api.put(`/admin/exercise-catalog/${editing.id}`, payload);
      else await api.post("/admin/exercise-catalog", payload);
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archivar este ejercicio? (no se borra, sólo se oculta)")) return;
    try { await api.delete(`/admin/exercise-catalog/${id}`); toast.success("Archivado"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const k = it.primary_muscle || "(sin grupo)";
      g[k] = g[k] || []; g[k].push(it);
    }
    return g;
  }, [items]);

  return (
    <AppShell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Catálogo de Ejercicios</h1>
          <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Biblioteca central de ejercicios enriquecidos. Alimenta el builder V2.</p>
        </div>
        <button onClick={startNew} style={btnPrimary} data-testid="catalog-new-btn"><Plus size={14}/> Nuevo ejercicio</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, padding: "8px 12px", flex: "1 1 280px" }}>
          <Search size={14} color="#a3a3a3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar por nombre…" style={{ background: "transparent", border: "none", color: "#fff", outline: "none", flex: 1, fontSize: 14 }} data-testid="catalog-search-input" />
          <button onClick={load} style={{ background: "transparent", border: "none", color: "#a3a3a3", cursor: "pointer", fontSize: 12 }}>Buscar</button>
        </div>
        <select value={muscle} onChange={(e) => setMuscle(e.target.value)} style={selectStyle} data-testid="catalog-filter-muscle">
          <option value="">Todos los músculos</option>
          {meta.muscle_groups.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={equipment} onChange={(e) => setEquipment(e.target.value)} style={selectStyle}>
          <option value="">Todo el material</option>
          {meta.equipment.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {editing && (
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.14)", padding: 20, borderRadius: 4, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 14, letterSpacing: "0.14em", textTransform: "uppercase" }}>{editing.id ? "Editar" : "Nuevo"} ejercicio</h3>
            <button onClick={() => setEditing(null)} style={{ background: "transparent", border: "none", color: "#a3a3a3", cursor: "pointer" }}><X size={18}/></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Nombre *</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} style={inputStyle} data-testid="catalog-name" />
            </div>
            <div>
              <label style={labelStyle}>Músculo principal</label>
              <select value={editing.primary_muscle || ""} onChange={(e) => setEditing({ ...editing, primary_muscle: e.target.value })} style={inputStyle}>
                <option value="">—</option>
                {meta.muscle_groups.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tipo de ejecución</label>
              <select value={editing.execution_type || ""} onChange={(e) => setEditing({ ...editing, execution_type: e.target.value })} style={inputStyle}>
                <option value="">—</option>
                {meta.execution_types.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Dificultad</label>
              <select value={editing.difficulty || ""} onChange={(e) => setEditing({ ...editing, difficulty: e.target.value })} style={inputStyle}>
                <option value="">—</option>
                {meta.difficulty.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tempo por defecto</label>
              <input value={editing.tempo_default || ""} onChange={(e) => setEditing({ ...editing, tempo_default: e.target.value })} placeholder="3-1-1-0" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Descanso por defecto</label>
              <input value={editing.rest_default || ""} onChange={(e) => setEditing({ ...editing, rest_default: e.target.value })} placeholder="90s" style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Vídeo (URL)</label>
              <input value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} placeholder="https://youtube.com/…" style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Grupos musculares</label>
              <div style={chipRow}>
                {meta.muscle_groups.map((m) => (
                  <button key={m} onClick={() => toggleArr("muscle_groups", m)} style={editing.muscle_groups?.includes(m) ? chipActive : chip} type="button">{m}</button>
                ))}
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Material</label>
              <div style={chipRow}>
                {meta.equipment.map((m) => (
                  <button key={m} onClick={() => toggleArr("equipment", m)} style={editing.equipment?.includes(m) ? chipActive : chip} type="button">{m.replace("_", " ")}</button>
                ))}
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Descripción</label>
              <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={btnPrimary} data-testid="catalog-save"><Save size={14}/> {saving ? "Guardando…" : "Guardar"}</button>
            <button onClick={() => setEditing(null)} style={btnGhost}>Cancelar</button>
          </div>
        </div>
      )}

      {loading && <div style={{ padding: 40, textAlign: "center", color: "#a3a3a3" }}>Cargando…</div>}
      {!loading && items.length === 0 && (
        <div style={{ padding: 60, textAlign: "center", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
          <Dumbbell size={40} color="#525252" style={{ marginBottom: 12 }} />
          <div style={{ color: "#a3a3a3" }}>Aún no hay ejercicios. Crea el primero para empezar tu biblioteca.</div>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([group, list]) => (
        <div key={group} style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.14em", fontSize: 18, color: "#dc2626", marginBottom: 10 }}>{group.toUpperCase()}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {list.map((it) => (
              <div key={it.id} style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 14, borderRadius: 4 }} data-testid={`catalog-item-${it.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{it.name}</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => startEdit(it)} style={iconBtn} title="Editar"><Edit3 size={13}/></button>
                    <button onClick={() => archive(it.id)} style={iconBtn} title="Archivar"><Archive size={13}/></button>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#a3a3a3", letterSpacing: "0.06em", marginBottom: 6 }}>
                  {it.execution_type && <span>{it.execution_type}</span>}
                  {it.difficulty && <span> · {it.difficulty}</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {(it.muscle_groups || []).map((g) => (
                    <span key={g} style={{ padding: "2px 7px", background: "rgba(220,38,38,0.14)", color: "#f87171", fontSize: 10, borderRadius: 999 }}>{g}</span>
                  ))}
                  {(it.equipment || []).map((eq) => (
                    <span key={eq} style={{ padding: "2px 7px", background: "rgba(255,255,255,0.06)", color: "#d4d4d4", fontSize: 10, borderRadius: 999 }}>{eq.replace("_", " ")}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </AppShell>
  );
}

const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 5 };
const inputStyle = { width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "9px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box" };
const selectStyle = { ...inputStyle, width: "auto", minWidth: 160, flex: "0 1 auto" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "9px 16px", fontSize: 13, letterSpacing: "0.06em", fontWeight: 600, cursor: "pointer", borderRadius: 2 };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#e5e5e5", border: "1px solid rgba(255,255,255,0.14)", padding: "9px 14px", fontSize: 13, cursor: "pointer", borderRadius: 2 };
const chipRow = { display: "flex", flexWrap: "wrap", gap: 6 };
const chip = { padding: "5px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#d4d4d4", fontSize: 12, cursor: "pointer", borderRadius: 999 };
const chipActive = { ...chip, background: "#dc2626", borderColor: "#dc2626", color: "#fff" };
const iconBtn = { background: "transparent", border: "none", color: "#a3a3a3", cursor: "pointer", padding: 4 };
